// SPDX-License-Identifier: AGPL-3.0-or-later

import i18n from '@app/app/I18n';
import {Routes} from '@app/app/Routes';
import Authentication from '@app/features/auth/state/Authentication';
import type {GatewayHandlerContext} from '@app/features/gateway/events/EventRouter';
import LobbyRealtime from '@app/features/lobby/state/LobbyRealtime';
import {getNotificationIconURL} from '@app/features/notification/utils/NotificationIconURL';
import * as NotificationUtils from '@app/features/notification/utils/NotificationUtils';
import {SoundType} from '@app/features/notification/utils/SoundUtils';
import * as SoundCommands from '@app/features/ui/commands/SoundCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {msg} from '@lingui/core/macro';

function readLobbyId(data: unknown): string | null {
	if (!data || typeof data !== 'object') return null;
	const record = data as {lobby_id?: unknown; id?: unknown; lobby?: {id?: unknown}};
	if (typeof record.lobby_id === 'string') return record.lobby_id;
	if (typeof record.id === 'string') return record.id;
	if (record.lobby && typeof record.lobby.id === 'string') return record.lobby.id;
	return null;
}

function bumpFromPayload(data: unknown): void {
	const lobbyId = readLobbyId(data);
	if (lobbyId) {
		LobbyRealtime.bumpLobby(lobbyId);
	}
	LobbyRealtime.bumpDiscovery();
}

export function handleLobbyUpdate(data: unknown, _context: GatewayHandlerContext): void {
	bumpFromPayload(data);
}

export function handleLobbyDelete(data: unknown, _context: GatewayHandlerContext): void {
	const lobbyId = readLobbyId(data);
	if (lobbyId) {
		LobbyRealtime.markLobbyRemoved(lobbyId);
		return;
	}
	LobbyRealtime.bumpDiscovery();
}

export function handleLobbyMemberAdd(data: unknown, _context: GatewayHandlerContext): void {
	bumpFromPayload(data);
	announceLobbyJoin(data);
}

const JOINED_TITLE = msg({
	message: 'New player in your lobby',
	comment: 'Notification title when someone joins a game lobby you are in.',
});
const JOINED_BODY = msg({
	message: '{name} joined the lobby',
	comment: 'Toast / notification body when a player joins your game lobby. Preserve {name}.',
});

/** Sound + toast (or a native notification when the app is in the background) for everyone else in the lobby. */
function announceLobbyJoin(data: unknown): void {
	if (!data || typeof data !== 'object') return;
	const payload = data as {
		lobby_id?: unknown;
		member?: {user?: {id?: unknown; username?: unknown; global_name?: unknown; avatar?: unknown}};
	};
	const user = payload.member?.user;
	if (!user || typeof user.id !== 'string' || user.id === Authentication.currentUserId) return;
	const name =
		(typeof user.global_name === 'string' && user.global_name) ||
		(typeof user.username === 'string' && user.username) ||
		'';
	const body = i18n._(JOINED_BODY, {name});
	SoundCommands.playSound(SoundType.UserJoin);
	if (document.hasFocus()) {
		ToastCommands.success(body);
		return;
	}
	const lobbyId = typeof payload.lobby_id === 'string' ? payload.lobby_id : null;
	void NotificationUtils.showNotification({
		id: `lobby-join:${lobbyId}:${user.id}`,
		title: i18n._(JOINED_TITLE),
		body,
		icon: getNotificationIconURL({id: user.id, avatar: typeof user.avatar === 'string' ? user.avatar : null}),
		url: lobbyId ? Routes.LOBBY_DETAIL.replace(':lobbyId', lobbyId) : undefined,
		playSound: false,
	}).catch(() => undefined);
}

export function handleLobbyMemberRemove(data: unknown, _context: GatewayHandlerContext): void {
	bumpFromPayload(data);
}

export function handleLobbyMessageCreate(data: unknown, _context: GatewayHandlerContext): void {
	bumpFromPayload(data);
}

export function handleLobbyRequestCreate(data: unknown, _context: GatewayHandlerContext): void {
	bumpFromPayload(data);
}

export function handleLobbyRequestDelete(data: unknown, _context: GatewayHandlerContext): void {
	bumpFromPayload(data);
}

export function handleLobbyRequestUpdate(data: unknown, _context: GatewayHandlerContext): void {
	bumpFromPayload(data);
}
